import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../../../components/PageHeaderWithBreadcrumbs";
import { UITableShell, UITableToolbar } from "../../../../../components/ui";
import ReportSummaryCard from "../../../reports/shared/ReportSummaryCard";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportShellSx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
} from "../../../reports/shared/reportLayout";
import { csvEscape } from "../utils/promoStoreOrderItems";
import PromoStoreReportPreviewDialog from "./PromoStoreReportPreviewDialog";
import { buildCatalogProductPerformance } from "./productPerformanceCatalog";
import "./promostore-reports.css";

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  search: "",
  orderStatus: "all",
  customer: "",
};

const ORDER_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "failed", label: "Failed" },
];

const currency = (value, code = "USD") => {
  const currencyCode = String(code || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode }).format(Number(value) || 0);
  } catch (_error) {
    return `${currencyCode} ${(Number(value) || 0).toFixed(2)}`;
  }
};

const TopSellersCard = ({ rows, currencyCode, expanded, onToggle, preview = false }) => {
  const visibleRows = rows.slice(0, expanded ? 5 : 1);

  return (
    <Paper className={`promostore-product-top-sellers${preview ? " is-preview" : ""}${expanded ? " is-expanded" : ""}`} elevation={0}>
      <Box className="promostore-product-top-sellers-heading">
        <Box className="promostore-product-top-sellers-title">
          <Box className="promostore-product-preview-kpi-icon-wrap">
            <EmojiEventsOutlinedIcon className="promostore-product-preview-kpi-icon" fontSize="small" />
          </Box>
          <Typography className="promostore-product-preview-kpi-label">{expanded ? "Top Sellers" : "Top Seller"}</Typography>
        </Box>
      </Box>
      {visibleRows.length ? (
        <Box className={`promostore-product-top-sellers-list${expanded ? " is-expanded" : ""}`}>
          {visibleRows.map((row, index) => (
            <Box className="promostore-product-top-seller" key={row.key || `${row.product}-${index}`}>
              <Typography className="promostore-product-top-seller-rank">{index + 1}.</Typography>
              <Typography className="promostore-product-top-seller-name">
                {row.displayProduct || row.product || "Unnamed item"}
              </Typography>
              <Box className="promostore-product-top-seller-results">
                <Typography className="promostore-product-top-seller-units">{row.unitsSold}</Typography>
                <Typography className="promostore-product-top-seller-revenue">{currency(row.revenue, currencyCode)}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : <Typography className="promostore-product-top-sellers-empty">No sellers available.</Typography>}
      {rows.length > 1 ? (
        <Button className="promostore-product-top-sellers-toggle" size="small" onClick={onToggle}>
          {expanded ? "View less ^" : "View more ›"}
        </Button>
      ) : null}
    </Paper>
  );
};

TopSellersCard.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  currencyCode: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  preview: PropTypes.bool,
};

const decodeDisplayText = (value = "") => {
  const text = String(value || "");
  if (!text) return "";
  if (typeof document === "undefined") return text;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

const displayOptionalValue = (value) => {
  if (value === 0 || value === "0") return value;
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized.toLowerCase() === "n/a") return "—";
  return value;
};

const normalizeImageUrl = (value) => {
  if (Array.isArray(value)) return value.map(normalizeImageUrl).find(Boolean) || "";
  if (value && typeof value === "object") {
    return normalizeImageUrl(value.src ?? value.url ?? value.ImageUrl ?? value.imageUrl);
  }
  return typeof value === "string" ? value.trim() : "";
};

const getProductPrimaryImage = (product = {}) => normalizeImageUrl(product?.ImageUrl)
  || normalizeImageUrl(product?.imageUrl);

const getProductCollectionImage = (product = {}) => normalizeImageUrl(product?.ImageUrls)
  || normalizeImageUrl(product?.imageUrls);

const normalizeCategory = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((category) => {
      if (category && typeof category === "object") return category.name ?? category.Name ?? "";
      return category;
    })
    .filter((category) => category !== undefined && category !== null && String(category).trim())
    .map((category) => String(category).trim())
    .join(", ");
};

const getProductCategory = (product = {}) => [product?.Category, product?.category, product?.Categories, product?.categories]
  .map(normalizeCategory)
  .find(Boolean) || "";

const enrichCatalogRows = (rows = []) => rows.map((row) => {
  const product = row.catalogProduct || {};
  return {
    ...row,
    displayProduct: String(product?.Name ?? product?.name ?? row.product ?? "").trim() || "Unnamed item",
    image: getProductPrimaryImage(product) || getProductCollectionImage(product) || "",
    category: getProductCategory(product),
  };
});

const neutralizeSpreadsheetFormula = (value) => (/^[=+\-@]/.test(String(value ?? "")) ? `'${value}` : value);

const downloadCsv = (rows = [], currencyCode = "USD") => {
  const headers = ["Product", "SKU", "Variation", "Orders", "Units Sold", "Unit Price", "Revenue", "Average Quantity per Order", "Image", "Category", "Items Quantity", "Items Left", "Trend", "Top Size", "Top Color"];
  const lines = rows.map((row) => [
    neutralizeSpreadsheetFormula(row.product),
    neutralizeSpreadsheetFormula(row.sku || "N/A"),
    neutralizeSpreadsheetFormula(row.variation || "N/A"),
    row.orders,
    row.unitsSold,
    row.unitPrice == null ? "N/A" : currency(row.unitPrice, currencyCode),
    currency(row.revenue, currencyCode),
    row.averageQuantityPerOrder.toFixed(2),
    neutralizeSpreadsheetFormula(row.image || "No Image"),
    neutralizeSpreadsheetFormula(row.category || "N/A"),
    row.itemsQuantity,
    neutralizeSpreadsheetFormula(row.itemsLeft ?? "N/A"),
    neutralizeSpreadsheetFormula(row.trend || "N/A"),
    neutralizeSpreadsheetFormula(row.topSize || "N/A"),
    neutralizeSpreadsheetFormula(row.topColor || "N/A"),
  ].map(csvEscape).join(","));
  const blob = new Blob([[headers.map(csvEscape).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `promostore-product-performance-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const InstancePromoStoreProductReport = ({ previewOpen = null, onPreviewClose = null, instanceUniqueId: instanceUniqueIdProp = null, promoStoreId = null, store: previewStore = null }) => {
  const routeParams = useParams();
  const instanceUniqueId = instanceUniqueIdProp || routeParams.instanceUniqueId;
  const id = promoStoreId || routeParams.id;
  const isPreview = previewOpen !== null;
  const [store, setStore] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [controlsExpanded, setControlsExpanded] = useState(true);
  const [trendsExpanded, setTrendsExpanded] = useState(true);
  const [topSellersExpanded, setTopSellersExpanded] = useState(false);
  const [generatedAt] = useState(() => new Date());

  useEffect(() => {
    if (isPreview && previewStore) {
      setStore(previewStore);
      setOrders(Array.isArray(previewStore.Orders) ? previewStore.Orders : []);
      setLoading(false);
      setError("");
      return undefined;
    }

    let ignore = false;
    setLoading(true);
    setError("");
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/get-promostore/${id}`)
      .then((response) => {
        if (ignore) return;
        if (!response.data?.success) throw new Error(response.data?.message || "Unable to load PromoStore orders.");
        const nextStore = response.data.promostore || {};
        const instanceIds = Array.isArray(nextStore.InstanceUniqueIds) ? nextStore.InstanceUniqueIds : [];
        if (instanceIds.length && !instanceIds.includes(instanceUniqueId)) {
          throw new Error("PromoStore not found for this instance.");
        }
        setStore(nextStore);
        setOrders(Array.isArray(nextStore.Orders) ? nextStore.Orders : []);
      })
      .catch((err) => {
        if (!ignore) setError(err?.response?.data?.message || err.message || "Unable to load Product Performance Report.");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => { ignore = true; };
  }, [id, instanceUniqueId, isPreview, previewStore]);

  const products = useMemo(() => (Array.isArray(store?.Products) ? store.Products : []), [store?.Products]);
  const report = useMemo(
    () => buildCatalogProductPerformance({
      products,
      orders,
      filters: appliedFilters,
      promoStoreId: id,
      currencyCode: store?.Currency ?? store?.currency ?? "USD",
    }),
    [appliedFilters, id, orders, products, store?.Currency, store?.currency]
  );
  const enrichedRows = useMemo(
    () => enrichCatalogRows(report.rows),
    [report.rows]
  );
  const topSellerRows = useMemo(() => enrichedRows.filter((row) => row.unitsSold > 0), [enrichedRows]);
  const pagedRows = useMemo(() => enrichedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [enrichedRows, page, rowsPerPage]);

  const applyFilters = useCallback(() => {
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      setError("Date From cannot be after Date To.");
      return;
    }
    setError("");
    setPage(0);
    setAppliedFilters(filters);
  }, [filters]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(0);
    setError("");
  }, []);

  const summaryCards = [
    { key: "products", label: "Total Products", value: report.summary.totalProducts, tone: "products", Icon: Inventory2OutlinedIcon },
    { key: "units", label: "Total Units Sold", value: report.summary.totalUnitsSold, tone: "units", Icon: ShoppingCartOutlinedIcon },
    { key: "revenue", label: "Product Revenue", value: currency(report.summary.productRevenue, report.currency), tone: "revenue", Icon: PaidOutlinedIcon },
    { key: "orders", label: "Total Orders", value: report.summary.totalOrders, tone: "orders", Icon: ReceiptLongOutlinedIcon },
    { key: "best", label: "Best Seller", value: report.summary.bestSeller, tone: "best", Icon: EmojiEventsOutlinedIcon },
  ];

  const closePreview = onPreviewClose || (() => {});
  const storeName = store?.ShopName || store?.shopName || store?.title || "PromoStore";
  const storeLogoUrl = store?.ShopLogoUrl || store?.shopLogoUrl || "";
  const generatedLabel = generatedAt.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const activeFilterSummary = [
    `Date: ${appliedFilters.dateFrom || "Any start"} – ${appliedFilters.dateTo || "Any end"}`,
    `Status: ${ORDER_STATUS_OPTIONS.find((option) => option.value === appliedFilters.orderStatus)?.label || "All statuses"}`,
    `Product/SKU: ${appliedFilters.search || "All"}`,
    `Customer: ${appliedFilters.customer || "All"}`,
  ].join(" | ");
  const bestSellerImage = topSellerRows[0]?.image || "";
  const averageOrderValue = report.summary.totalOrders > 0 ? report.summary.productRevenue / report.summary.totalOrders : 0;
  const metricSummaryCards = summaryCards.filter((card) => card.key !== "best");

  const previewReportContent = (
    <Box className="promostore-product-preview">
      <Box className="promostore-product-preview-header">
        <Box className="promostore-product-preview-brand">
          <img className="promostore-product-preview-elite-logo" src="/logo.png" alt="Elite Promotional Marketing" />
          <Box>
            <Typography variant="h6" fontWeight={800}>Product Performance Report</Typography>
            <Typography className="promostore-product-preview-store-line">
              <strong>Store:</strong> {storeName}
            </Typography>
          </Box>
        </Box>
        <Box className="promostore-product-preview-meta-wrap">
          {storeLogoUrl ? (
            <img
              className="promostore-product-preview-store-logo"
              src={storeLogoUrl}
              alt={`${storeName} logo`}
              onError={(event) => { event.currentTarget.style.display = "none"; }}
            />
          ) : null}
          <Box className="promostore-product-preview-meta">
            <Typography><strong>Generated:</strong> {generatedLabel}</Typography>
          </Box>
          <Tooltip title="Close report preview">
            <IconButton size="small" onClick={closePreview} aria-label="Close Product Performance Report">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper className="promostore-product-preview-controls" elevation={0}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle2" fontWeight={800}>Report Controls</Typography>
            <Typography className="promostore-product-preview-filter-summary">{activeFilterSummary}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" className="promostore-product-preview-control-actions">
            <Button variant="outlined" size="small" onClick={resetFilters} disabled={loading}>Reset</Button>
            <Button variant="outlined" size="small" onClick={applyFilters} disabled={loading}>Get Report</Button>
            <Tooltip title={controlsExpanded ? "Collapse controls" : "Expand controls"}>
              <IconButton size="small" onClick={() => setControlsExpanded((current) => !current)} aria-label={controlsExpanded ? "Collapse report controls" : "Expand report controls"}>
                {controlsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Collapse in={controlsExpanded}>
          <Box className="promostore-product-preview-filter-grid">
            <TextField label="Date From" type="date" size="small" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Date To" type="date" size="small" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Product or SKU" size="small" value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} />
            <TextField select label="Order Status" size="small" value={filters.orderStatus} onChange={(event) => setFilters((prev) => ({ ...prev, orderStatus: event.target.value }))}>
              {ORDER_STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <TextField label="Customer" size="small" value={filters.customer} onChange={(event) => setFilters((prev) => ({ ...prev, customer: event.target.value }))} />
          </Box>
        </Collapse>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <Box className="promostore-product-preview-loading"><CircularProgress /></Box> : null}
      {!loading && !orders.length ? <Alert severity="info">No orders are available for this PromoStore yet.</Alert> : null}

      {!loading && !error ? (
        <>
          <Box className="promostore-product-preview-kpis">
            {metricSummaryCards.map(({ Icon, ...card }) => (
              <Paper key={card.key} className={`promostore-product-preview-kpi is-${card.tone}`} elevation={0}>
                <Box className="promostore-product-preview-kpi-icon-wrap">
                  <Icon className="promostore-product-preview-kpi-icon" fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography className="promostore-product-preview-kpi-label">{card.label}</Typography>
                  <Tooltip title={String(card.value || "")}>
                    <Typography className="promostore-product-preview-kpi-value">{card.value}</Typography>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
            <TopSellersCard
              rows={topSellerRows}
              currencyCode={report.currency}
              expanded={topSellersExpanded}
              onToggle={() => setTopSellersExpanded((current) => !current)}
              preview
            />
            <Paper className="promostore-product-preview-kpi is-best promostore-product-best-seller-print" elevation={0}>
              <Box className="promostore-product-preview-kpi-icon-wrap">
                <EmojiEventsOutlinedIcon className="promostore-product-preview-kpi-icon" fontSize="small" />
              </Box>
              {bestSellerImage ? <img className="promostore-product-preview-kpi-image" src={bestSellerImage} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
              <Box minWidth={0}>
                <Typography className="promostore-product-preview-kpi-label">Best Seller</Typography>
                <Typography className="promostore-product-preview-kpi-value">{report.summary.bestSeller}</Typography>
                {report.rows[0]?.unitsSold ? <Typography className="promostore-product-preview-kpi-secondary">{report.rows[0].unitsSold} units · {currency(report.rows[0].revenue, report.currency)}</Typography> : null}
              </Box>
            </Paper>
          </Box>

          <Paper className="promostore-product-preview-trends" elevation={0}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: trendsExpanded ? 1 : 0 }}>
              <Typography variant="subtitle2" fontWeight={800}>Trend Summary</Typography>
              <Tooltip title={trendsExpanded ? "Collapse trends" : "Expand trends"}>
                <IconButton size="small" onClick={() => setTrendsExpanded((current) => !current)} aria-label={trendsExpanded ? "Collapse trends" : "Expand trends"}>
                  {trendsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Collapse in={trendsExpanded}>
              <Box className="promostore-product-preview-trend-grid">
                <Box className="promostore-product-preview-chart">
                  <Typography className="promostore-product-preview-section-label">Units Sold Trend</Typography>
                  <ResponsiveContainer width="100%" height={175}><LineChart data={report.trend}><XAxis dataKey="label" /><YAxis allowDecimals={false} width={34} /><RechartsTooltip /><Line type="monotone" dataKey="unitsSold" stroke="#c3171d" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
                </Box>
                <Box className="promostore-product-preview-chart">
                  <Typography className="promostore-product-preview-section-label">Revenue Trend</Typography>
                  <ResponsiveContainer width="100%" height={175}><LineChart data={report.trend}><XAxis dataKey="label" /><YAxis width={44} /><RechartsTooltip formatter={(value) => currency(value, report.currency)} /><Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
                </Box>
                <Box className="promostore-product-preview-trend-summary">
                  <Typography className="promostore-product-preview-section-label">Trend Summary</Typography>
                  <Box className="promostore-product-preview-neutral-state">
                    <AssessmentOutlinedIcon fontSize="small" />
                    <Typography>Prior-period comparison is not available for this report.</Typography>
                  </Box>
                </Box>
                <Box className="promostore-product-preview-totals-summary">
                  <Typography className="promostore-product-preview-section-label">Current Totals</Typography>
                  {[
                    ["Units Sold", report.summary.totalUnitsSold],
                    ["Revenue", currency(report.summary.productRevenue, report.currency)],
                    ["Orders", report.summary.totalOrders],
                    ["Average Order Value", currency(averageOrderValue, report.currency)],
                  ].map(([label, value]) => (
                    <Box key={label} className="promostore-product-preview-trend-row">
                      <Typography>{label}</Typography>
                      <Typography>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {!enrichedRows.length ? <Alert severity="info">No catalog products match the current Product/SKU filter.</Alert> : null}

          <Paper className="promostore-product-preview-table-card promostore-product-screen-table" elevation={0}>
            <TableContainer className="promostore-product-preview-table-container">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="promostore-product-preview-index-cell" align="center">#</TableCell>
                    <TableCell className="promostore-product-preview-image-cell" align="center">Image</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Unit Price</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Orders</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Line Count</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Units Sold</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Items Left</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Revenue</TableCell>
                    <TableCell className="promostore-product-preview-numeric-cell" align="right">Average Qty / Order</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>Top Size</TableCell>
                    <TableCell>Top Color</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row, rowIndex) => (
                    <TableRow key={row.groupKey} hover>
                      <TableCell className="promostore-product-preview-index-cell" align="center">{page * rowsPerPage + rowIndex + 1}</TableCell>
                      <TableCell className="promostore-product-preview-image-cell">
                        <Box className="promostore-product-preview-image-frame">
                          <ImageNotSupportedOutlinedIcon className="promostore-product-preview-image-placeholder" fontSize="small" aria-label="No product image" />
                          {row.image ? <img src={row.image} alt="" loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
                        </Box>
                      </TableCell>
                      <TableCell className="promostore-product-preview-product-cell">
                        <Tooltip title={decodeDisplayText(row.displayProduct || row.product || "Unnamed item")}>
                          <span>{decodeDisplayText(row.displayProduct || row.product || "Unnamed item")}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{displayOptionalValue(row.sku)}</TableCell>
                      <TableCell>{displayOptionalValue(row.category)}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{row.unitPrice == null ? "—" : currency(row.unitPrice, report.currency)}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{row.orders}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{row.itemsQuantity}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{row.unitsSold}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{displayOptionalValue(row.itemsLeft)}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{currency(row.revenue, report.currency)}</TableCell>
                      <TableCell className="promostore-product-preview-numeric-cell" align="right">{row.averageQuantityPerOrder.toFixed(2)}</TableCell>
                      <TableCell>{displayOptionalValue(row.trend)}</TableCell>
                      <TableCell>{displayOptionalValue(row.topSize)}</TableCell>
                      <TableCell>{displayOptionalValue(row.topColor)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={enrichedRows.length}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>

          <Box className="promostore-product-print-table" aria-hidden="true">
            <Typography variant="subtitle1" fontWeight={800}>Product Performance Summary</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Product", "SKU", "Category", "Unit Price", "Orders", "Line Count", "Units Sold", "Items Left", "Revenue", "Average Qty / Order", "Trend", "Top Size", "Top Color"].map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {enrichedRows.map((row, rowIndex) => (
                  <TableRow key={`print-${row.groupKey}`}>
                    <TableCell>{rowIndex + 1}</TableCell>
                    <TableCell>{decodeDisplayText(row.product || "Unnamed item")}</TableCell>
                    <TableCell>{displayOptionalValue(row.sku)}</TableCell>
                    <TableCell>{displayOptionalValue(row.category)}</TableCell>
                    <TableCell>{row.unitPrice == null ? "—" : currency(row.unitPrice, report.currency)}</TableCell>
                    <TableCell>{row.orders}</TableCell>
                    <TableCell>{row.itemsQuantity}</TableCell>
                    <TableCell>{row.unitsSold}</TableCell>
                    <TableCell>{displayOptionalValue(row.itemsLeft)}</TableCell>
                    <TableCell>{currency(row.revenue, report.currency)}</TableCell>
                    <TableCell>{row.averageQuantityPerOrder.toFixed(2)}</TableCell>
                    <TableCell>{displayOptionalValue(row.trend)}</TableCell>
                    <TableCell>{displayOptionalValue(row.topSize)}</TableCell>
                    <TableCell>{displayOptionalValue(row.topColor)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography className="promostore-product-print-footer">
              EliteCore · Generated {generatedLabel}
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );

  const reportContent = (
            <Box sx={reportContentSx}>
              <Paper className="promostore-report-controls" elevation={0} sx={reportFilterCardSx}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5} sx={{ mb: 1.5 }} flexWrap="wrap">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>Report Controls</Typography>
                    <Typography variant="body2" color="text.secondary">Filter synchronized PromoStore order lines for this store only.</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" size="small" onClick={resetFilters} disabled={loading}>Reset</Button>
                    <Button variant="outlined" size="small" onClick={applyFilters} disabled={loading}>Get Report</Button>
                  </Stack>
                </Stack>
                <Box sx={reportFilterGridSx}>
                  <TextField label="Date From" type="date" size="small" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} />
                  <TextField label="Date To" type="date" size="small" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} />
                  <TextField label="Product or SKU" size="small" value={filters.search} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} />
                  <TextField select label="Order Status" size="small" value={filters.orderStatus} onChange={(event) => setFilters((prev) => ({ ...prev, orderStatus: event.target.value }))}>
                    {ORDER_STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </TextField>
                  <TextField label="Customer" size="small" value={filters.customer} onChange={(event) => setFilters((prev) => ({ ...prev, customer: event.target.value }))} />
                </Box>
              </Paper>

              {error ? <Alert severity="error">{error}</Alert> : null}
              {loading ? <Box sx={{ minHeight: 220, display: "grid", placeItems: "center" }}><CircularProgress /></Box> : null}
              {!loading && !orders.length ? <Alert severity="info">No orders are available for this PromoStore yet.</Alert> : null}

              {!loading && !error ? (
                <>
                  <Box className="promostore-product-summary-grid">
                    {metricSummaryCards.map((card) => <ReportSummaryCard key={card.key} label={card.label} value={card.value} tone={card.tone} />)}
                    <TopSellersCard
                      rows={topSellerRows}
                      currencyCode={report.currency}
                      expanded={topSellersExpanded}
                      onToggle={() => setTopSellersExpanded((current) => !current)}
                    />
                  </Box>

                  <Box className="promostore-product-chart-grid">
                    <Paper elevation={0} sx={{ ...reportFilterCardSx, height: 260 }}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Units Sold Trend</Typography>
                      <ResponsiveContainer width="100%" height="85%"><LineChart data={report.trend}><XAxis dataKey="label" /><YAxis allowDecimals={false} /><RechartsTooltip /><Line type="monotone" dataKey="unitsSold" stroke="#c3171d" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
                    </Paper>
                    <Paper elevation={0} sx={{ ...reportFilterCardSx, height: 260 }}>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Revenue Trend</Typography>
                      <ResponsiveContainer width="100%" height="85%"><LineChart data={report.trend}><XAxis dataKey="label" /><YAxis /><RechartsTooltip formatter={(value) => currency(value, report.currency)} /><Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
                    </Paper>
                  </Box>

                  {!enrichedRows.length ? <Alert severity="info">No catalog products match the current Product/SKU filter.</Alert> : null}

                  <Paper elevation={0} sx={reportTablePaperSx}>
                    <TableContainer sx={reportTableContainerSx}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {["Product", "SKU", "Variation", "Orders", "Units Sold", "Unit Price", "Revenue", "Average Quantity per Order"].map((header) => <TableCell key={header} sx={reportTableHeaderCellSx}>{header}</TableCell>)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pagedRows.map((row) => (
                            <TableRow key={row.groupKey} hover>
                              <TableCell>{row.displayProduct || row.product || "Unnamed item"}</TableCell>
                              <TableCell>{row.sku || "N/A"}</TableCell>
                              <TableCell>{row.variation || "N/A"}</TableCell>
                              <TableCell>{row.orders}</TableCell>
                              <TableCell>{row.unitsSold}</TableCell>
                              <TableCell>{row.unitPrice == null ? "—" : currency(row.unitPrice, report.currency)}</TableCell>
                              <TableCell>{currency(row.revenue, report.currency)}</TableCell>
                              <TableCell>{row.averageQuantityPerOrder.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={report.rows.length}
                      page={page}
                      onPageChange={(_event, nextPage) => setPage(nextPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(Number(event.target.value));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </Paper>
                </>
              ) : null}
            </Box>
  );

  if (isPreview) {
    return (
      <PromoStoreReportPreviewDialog
        open={Boolean(previewOpen)}
        onClose={closePreview}
        title={`Product Performance Report Preview (${report.rows.length} products)`}
        titleSx={{ position: "absolute", width: 1, height: 1, p: 0, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}
        paperSx={{ width: "97vw", maxWidth: "97vw", height: "94vh", maxHeight: "94vh" }}
        contentSx={{ display: "flex", minHeight: 0, overflow: "hidden", pt: 2 }}
        actions={(
          <>
            <Button onClick={closePreview}>Close</Button>
            <Button variant="outlined" disabled={!enrichedRows.length} onClick={() => downloadCsv(enrichedRows, report.currency)}>
              Download CSV
            </Button>
            <Button variant="contained" onClick={() => window.print()}>
              Print Report
            </Button>
          </>
        )}
      >
        {previewReportContent}
      </PromoStoreReportPreviewDialog>
    );
  }

  return (
    <div className="instance-dashboard promostore-product-report">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <PageHeaderWithBreadcrumbs title="Product Performance Report" breadcrumb="Dashboard > PromoStores > Product Performance Report" includeButton={false} />
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Box className="promostore-report-page-header" sx={{ width: "100%" }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>Product Performance Report</Typography>
                  <Typography className="promostore-product-report-print-range">
                    {store?.ShopName || store?.shopName || store?.title || "PromoStore"} · {appliedFilters.dateFrom || "Any start"} to {appliedFilters.dateTo || "Any end"}
                  </Typography>
                </Box>
                <Stack className="promostore-report-actions" direction="row" spacing={1}>
                  <Button variant="contained" size="small" startIcon={<DownloadIcon />} disabled={!enrichedRows.length} onClick={() => downloadCsv(enrichedRows, report.currency)}>
                    Export CSV
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>
                    Print Report
                  </Button>
                </Stack>
              </Box>
            </UITableToolbar>
            {reportContent}
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

InstancePromoStoreProductReport.propTypes = {
  previewOpen: PropTypes.bool,
  onPreviewClose: PropTypes.func,
  instanceUniqueId: PropTypes.string,
  promoStoreId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  store: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Orders: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

InstancePromoStoreProductReport.defaultProps = {
  previewOpen: null,
  onPreviewClose: null,
  instanceUniqueId: null,
  promoStoreId: null,
  store: null,
};

export default InstancePromoStoreProductReport;
